import { Component, computed, DestroyRef, inject, input, linkedSignal, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { RouterLinkWithLang } from '../../directives/router-link-with-lang/router-link-with-lang.directive';
import type { ProductListItemDto, ProductVariantDto, ProductVariantGroupDto } from '@api/models';
import { ProductLabelTypeEnum } from '@api/models';
import { CartService } from '../../services/cart.service';
import { CustomerService } from '../../services/customer.service';
import { WishlistService } from '../../services/wishlist.service';
import { IconComponent } from "../icon/icon.component";
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { PreloaderComponent } from '../preloader/preloader.component';
import { ProductVariantsComponent } from '../product-variants/product-variants.component';
import { ProductDiscountsComponent } from '../product-discounts/product-discounts.component';
import { RatingStarsComponent } from '../rating-stars/rating-stars.component';
import { resolveActivePriceIndex } from '../../helpers/resolve-active-price-index.function';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

@Component({
  selector: 'product-list-item',
  templateUrl: './product-list-item.component.html',
  styleUrls: ['./product-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProductVariantsComponent,
    ProductDiscountsComponent,
    RatingStarsComponent,
    PreloaderComponent,
    TranslatePipe,
    RouterLink,
    RouterLinkWithLang,
    IconComponent,
  ],
})
export class ProductListItemComponent {
  protected readonly customerService = inject(CustomerService);
  protected readonly wishlistService = inject(WishlistService);
  private dialog = inject(MatDialog);
  protected readonly cartService = inject(CartService);
  private readonly destroyRef = inject(DestroyRef);

  readonly ProductLabelTypeEnum = ProductLabelTypeEnum;

  readonly item = input<ProductListItemDto>(undefined);
  readonly parentNameForAnalytics = input.required<string>();
  // The list's grid lines card bottoms up across a row, so the name needn't hold three lines for that
  readonly isInListGrid = input(false);
  readonly showCart = output<number>();

  /**
   * The swatch last hovered on a variant group's card. It sticks until another swatch is hovered,
   * so the variant on screen is the one Купить adds; a new item starts over.
   */
  protected readonly hoveredVariant = linkedSignal<ProductListItemDto, ProductVariantDto | null>({
    source: this.item,
    computation: () => null,
  });

  /** The item, or the hovered variant over it; the label, rating and the rest stay the group's */
  protected readonly shown = computed<ProductListItemDto>(() => {
    const item = this.item();
    const variant = this.hoveredVariant();
    if (!variant || variant.productId === item.id) {
      return item;
    }
    return {
      ...item,
      id: variant.productId,
      slug: variant.slug,
      sku: variant.sku,
      name: variant.name,
      mediaUrl: variant.mediaUrl,
      mediaHoverUrl: null,
      price: variant.price,
      oldPrice: variant.oldPrice,
      discountedPrices: variant.discountedPrices,
      isInStock: variant.isInStock,
    };
  });

  /**
   * The swatch of the variant on screen is the selected one. A row the shown variant has no swatch in
   * keeps its own selection: hovering a colour does not unselect the volume.
   */
  protected readonly variantGroups = computed<ProductVariantGroupDto[]>(() => {
    const shownId = this.shown().id;
    return (this.item().variantGroups ?? []).map(group => {
      if (!group.variants.some(variant => variant.productId === shownId)) {
        return group;
      }
      const variants = group.variants.map(variant => ({ ...variant, isSelected: variant.productId === shownId }));
      return { ...group, variants };
    });
  });

  error = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  discountValue = computed<number | undefined>(() => {
    const { oldPrice, price } = this.shown();
    return oldPrice ? Math.ceil((oldPrice - price) / oldPrice * 100) : undefined;
  });
  activePriceIndex = computed<number | undefined>(() => {
    return resolveActivePriceIndex(this.shown(), this.cartService.cartPrices()?.itemsCost);
  });

  openCart(): void {
    this.showCart.emit(this.shown().id);
  }

  addToCart(): void {
    this.error.set(null);
    this.isLoading.set(true);

    const shown = this.shown();
    this.cartService.setCartItem({
      productId: shown.id,
      qty: 1,
      analyticsData: {
        sku: shown.sku,
        name: shown.name,
        price: shown.price,
        source: this.parentNameForAnalytics(),
      },
    })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.openCart(),
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }

  toggleWishlistFlag(): void {
    const shown = this.shown();
    this.wishlistService.toggleWishlistItem(
      shown.id,
      {
        sku: shown.sku,
        name: shown.name,
        price: shown.price,
        source: this.parentNameForAnalytics(),
      },
    );
  }

  setItemThumbnail(): string {
    const item = this.shown();
    if (!item.mediaUrl) {
      return '/assets/images/no-img.jpg';
    } else {
      return item.mediaUrl;
    }
  }

  suggestToLogin(): void {
    this.dialog.open(LoginModalComponent);
  }
}
